/**
 * ImageBlobResizer resizes image contained in the inputBlob and generate a proportionally resized image,
 * converts the image into Blob and pass blob to the parent.
 *
 * It does so by drawing a HTMLImageElement on canvas and manipulate canvas there,
 * then outputs a Blob containing the resized .jpg generated.
 * The resulting Blob object is passed to parent via callback.
 */

import * as React from 'react';

import { Helpers } from '../../ts/helpers/Helpers';
import { ImagePropertiesHelper, Orientation } from '../../ts/helpers/ImagePropertiesHelper';

export class ImageBlobResizer extends React.Component<ImageBlobResizer.Props, ImageBlobResizer.State> {

  private imageDom: HTMLImageElement;
  private canvasDom: HTMLCanvasElement;
  private intermediateCanvasDom: HTMLCanvasElement;

  public constructor(props: ImageBlobResizer.Props) {
    super(props);
    this.state = {
      inputImageDataUrl: null,
      orientation: Orientation.RotateNoneFlipNone
    };
  }

  public componentWillReceiveProps(nextProps: ImageBlobResizer.Props) {
    const newInputBlob = nextProps.inputBlob;
    if (newInputBlob && newInputBlob.type.match('image.*')) {
      ImagePropertiesHelper.getImageOrientation(nextProps.inputBlob)
        .then((orientation: Orientation) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            this.setState({
              inputImageDataUrl: reader.result as string,
              orientation: orientation
            });
          };
          reader.readAsDataURL(newInputBlob);
        });
    }
  }

  public render(): JSX.Element {
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <div style={{display: 'none'}}>
        <img ref={(ref) => this.imageDom = ref}
             src={this.state.inputImageDataUrl}/>
        <canvas ref={(ref) => this.canvasDom = ref}/>
        <canvas ref={(ref) => this.intermediateCanvasDom = ref}/>
      </div>
    );
  }

  public componentDidUpdate() {
    if (!this.state.inputImageDataUrl) {
      return;
    }
    this.imageDom.onload = () => {
      this.drawResizedImageOnCanvas(this.canvasDom, this.intermediateCanvasDom, this.imageDom);
      this.canvasDom.toBlob(
        (blob: Blob) => this.props.handleResizeComplete(blob),
        'image/jpeg', 1
      );
    };
  }

  // To scale down images on canvas with better interpolation and avoid pixelation,
  // we step down several times instead of directly scale the input image to a small scale
  private drawResizedImageOnCanvas(canvasDom: HTMLCanvasElement, intermediateCanvasDomDom: HTMLCanvasElement, imageDom: HTMLImageElement) {
    const orientation = this.state.orientation;

    if (this.props.inputBlob.size <= this.props.maxImageSizeLimit) {
      canvasDom.width = imageDom.width;
      canvasDom.height = imageDom.height;

      const context = canvasDom.getContext('2d');
      this.applyRotationToCanvasContext(orientation, context, canvasDom.width, canvasDom.height);

      context.drawImage(imageDom, 0, 0);
      return;
    }

    // if blob size exceeds maxImageSizeLimit, perform resize
    const targetSizes: Helpers.ImageDimensions = Helpers.calculateResizedDimensions(imageDom.width, imageDom.height,
      this.props.maxWidth, this.props.maxHeight);

    // step1: resize image by half and draw on canvas2
    const intermediateCanvasDomContext = intermediateCanvasDomDom.getContext('2d');
    intermediateCanvasDomDom.width = imageDom.width * 0.5;
    intermediateCanvasDomDom.height = imageDom.height * 0.5;
    intermediateCanvasDomContext.drawImage(
      imageDom,
      0, 0, intermediateCanvasDomDom.width, intermediateCanvasDomDom.height
    );

    // step2: resize image further by repeatedly redrawing downsized intermediateCanvasDom
    const steps = Math.floor(Math.log(imageDom.width / targetSizes.width) / Math.log(2));
    let currWidth = intermediateCanvasDomDom.width;
    let currHeight = intermediateCanvasDomDom.height;
    for (let i = 0; i < steps; ++i) {
      intermediateCanvasDomContext.drawImage(
        intermediateCanvasDomDom,
        0, 0, intermediateCanvasDomDom.width * 0.5, intermediateCanvasDomDom.height * 0.5
      );
      currWidth /= 2;
      currHeight /= 2;
    }

    // step3: finally crop the resized image from canvas2 and draw on canvas1
    // Set up target width and height for canvasDom1 so the output image will be exactly of that dimensions
    canvasDom.width = targetSizes.width;
    canvasDom.height = targetSizes.height;
    const canvasDomContext = canvasDom.getContext('2d');

    // Rotate the image if needed
    this.applyRotationToCanvasContext(orientation, canvasDomContext, canvasDom.width, canvasDom.height);
    canvasDomContext.drawImage(
      intermediateCanvasDomDom,
      0, 0, currWidth, currHeight,
      0, 0, canvasDom.width, canvasDom.height
    );
  }

  private applyRotationToCanvasContext(orientation: Orientation = Orientation.RotateNoneFlipNone, context: CanvasRenderingContext2D, origWidth: number, origHeight: number) {
    let newHeight: number = origHeight, newWidth: number = origWidth;

    if (shouldFlipHeightAndWidth(orientation)) {
      newWidth = origHeight;
      newHeight = origWidth;
    }
    switch (orientation) {
      case Orientation.RotateNoneFlipX: context.transform(-1, 0, 0, 1, newWidth, 0); break;
      case Orientation.Rotate180FlipNone: context.transform(-1, 0, 0, -1, newWidth, newHeight); break;
      case Orientation.Rotate180FlipX: context.transform(1, 0, 0, -1, 0, newHeight); break;
      case Orientation.Rotate90FlipX: context.transform(0, 1, 1, 0, 0, 0); break;
      case Orientation.Rotate90FlipNone: context.transform(0, 1, -1, 0, newHeight, 0); break;
      case Orientation.Rotate270FlipX: context.transform(0, -1, -1, 0, newHeight, newWidth); break;
      case Orientation.Rotate270FlipNone: context.transform(0, -1, 1, 0, 0, newWidth); break;
      default: break;
    }

    function shouldFlipHeightAndWidth(orientation: Orientation): boolean {
      // If orientation is between 5 and 8. https://i.stack.imgur.com/VGsAj.gif
      if (orientation === Orientation.Rotate90FlipX ||
          orientation === Orientation.Rotate90FlipNone ||
          orientation === Orientation.Rotate270FlipX ||
          orientation === Orientation.Rotate270FlipNone) {
        return true;
      }
      return false;
    }
  }
}

export module ImageBlobResizer {

  export interface Props {
    inputBlob: Blob;
    handleResizeComplete: (file: Blob) => void;
    maxImageSizeLimit: number;
    maxWidth: number;
    maxHeight: number;
  }

  export interface State {
    inputImageDataUrl: string;
    orientation?: number;
  }

}
